<template>
  <div class="case-digital-mlpa-main-data-chart-page col-block width-full" :style="{ flex: 1 }">
    <div class="case-digital-mlpa-main-data-chart-container col-block width-full" :style="{ flex: 1 }">
      <vueCharts
        :ref="'mlpa-chart'"
        autoresize
        :option="chartOption[panelData.chart_view.type]"
        :style="{ width: '100%', height: '100%' }"
        @click="e => chartOnClick(e)"
        @mouseover="e => chartOnMouseover(e)"
        @mouseout="e => chartOnMouseout(e)"
        @rendered="e => chartOnRendered(e)"
      ></vueCharts>
    </div>
  </div>
</template>

<script>
import vueCharts from 'vue-echarts'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import {
  ScatterChart,
  CustomChart
} from 'echarts/charts'
import {
  TooltipComponent,
  GridComponent,
  MarkLineComponent
} from 'echarts/components'
use([
  CanvasRenderer,
  ScatterChart,
  CustomChart,
  TooltipComponent,
  GridComponent,
  MarkLineComponent
])

export default {
  components: {
    vueCharts
  },
  props: {
    panelData: {
      type: Object,
      required: true
    },
    selectData: {
      type: Object,
      default: () => null
    }
  },
  computed: {
    chartDatas () {
      let panelData = this.panelData || {}
      let chartDataMap = ((panelData.chart_view || {}).data || []).reduce((map, data) => ({ ...map, [data['Probe Number']]: data }), {})
      return (panelData.fragment_table || []).map(data => chartDataMap[data['Probe Number']])
    },
    // chart
    chartOption () {
      return {
        'fragment_chart': {
          xAxis: [
            {
              type: 'category',
              name: this.$t('COMPONENT.digitalMLPA.mainDataChart.chart.axis.title.___Probe'),
              nameLocation: 'middle',
              nameGap: 60,
              nameTextStyle: {
                fontWeight: 'bold'
              },
              axisLabel: {
                fontWeight: 'bold',
                margin: 10,
                rotate: 90
              },
              axisTick: {
                alignWithLabel: true
              },
              boundaryGap: true,
              data: this.chartDatas.map(data => data['Probe Number'])
            },
            {
              position: 'top',
              axisLabel: {
                show: false
              },
              axisTick: {
                show: false
              },
              axisLine: {
                show: true,
                onZero: false
              }
            }
          ],
          yAxis: [
            {
              type: 'value',
              name: this.$t('COMPONENT.digitalMLPA.mainDataChart.chart.axis.title.___Copy_Number_Ratio'),
              nameLocation: 'middle',
              nameGap: 40,
              nameTextStyle: {
                fontWeight: 'bold'
              },
              max: 2,
              min: 0,
              interval: 0.25,
              axisLabel: {
                fontWeight: 'bold',
                formatter: value => value.toFixed(2)
              },
              axisLine: {
                show: true
              },
              splitLine: {
                lineStyle: {
                  color: '#DEDEDE'
                }
              }
            },
            {
              position: 'right',
              axisLabel: {
                show: false
              },
              axisTick: {
                show: false
              },
              axisLine: {
                show: true
              }
            }
          ],
          grid: {
            left: 20,
            right: 5,
            top: 10,
            bottom: 15,
            containLabel: true
          },
          tooltip: {
            trigger: 'axis',
            showContent: false,
            axisPointer: {
              type: 'shadow',
              shadowStyle: {
                color: 'rgba(0, 82, 140, 0.08)'
              }
            }
          },
          series: [
            {
              type: 'custom',
              silent: true,
              animation: false,
              renderItem: (params, api) => {
                let items = []
                const lineData = {
                  length: 15,
                  weight: 1.5
                }
                let category = api.value(0)
                // reference
                if (api.value(1) && api.value(2)) {
                  let referencePoints = {
                    lower: api.coord([category, api.value(1)]),
                    upper: api.coord([category, api.value(2)]),
                    center: api.coord([category, (api.value(1) - -api.value(2)) / 2])
                  }
                  items = [
                    ...items,
                    ...[
                      {
                        type: 'rect',
                        shape: {
                          x: referencePoints.lower[0] - (lineData.length / 2),
                          y: referencePoints.lower[1] - (lineData.weight / 2),
                          width: lineData.length,
                          height: lineData.weight
                        },
                        style: {
                          fill: '#6D6D6D'
                        }
                      },
                      {
                        type: 'rect',
                        shape: {
                          x: referencePoints.upper[0] - (lineData.length / 2),
                          y: referencePoints.upper[1] - (lineData.weight / 2),
                          width: lineData.length,
                          height: lineData.weight
                        },
                        style: {
                          fill: '#6D6D6D'
                        }
                      },
                      {
                        type: 'rect',
                        shape: {
                          x: referencePoints.lower[0] - (lineData.weight / 2),
                          y: referencePoints.lower[1] - (lineData.weight / 2),
                          width: 2,
                          height: referencePoints.upper[1] - referencePoints.lower[1] + lineData.weight
                        },
                        style: {
                          fill: '#6D6D6D'
                        }
                      },
                      {
                        type: 'circle',
                        shape: {
                          cx: referencePoints.center[0],
                          cy: referencePoints.center[1],
                          r: 4
                        },
                        style: {
                          fill: '#6D6D6D'
                        }
                      }
                    ]
                  ]
                }
                // expected
                if (api.value(3) && api.value(4)) {
                  let expectedPoints = {
                    min: api.coord([category, api.value(3)]),
                    max: api.coord([category, api.value(4)])
                  }
                  items = [
                    ...items,
                    ...[
                      {
                        type: 'rect',
                        shape: {
                          x: expectedPoints.min[0] - (lineData.length / 2),
                          y: expectedPoints.min[1] - (lineData.weight / 2),
                          width: lineData.length,
                          height: lineData.weight
                        },
                        style: {
                          fill: '#FB000C'
                        }
                      },
                      {
                        type: 'rect',
                        shape: {
                          x: expectedPoints.max[0] - (lineData.length / 2),
                          y: expectedPoints.max[1] - (lineData.weight / 2),
                          width: lineData.length,
                          height: lineData.weight
                        },
                        style: {
                          fill: '#0000FF'
                        }
                      }
                    ]
                  ]
                }
                // result
                if (api.value(5)) {
                  let resultPoint = api.coord([category, api.value(5)])
                  let aberrant = api.value(6)
                  items = [
                    ...items,
                    {
                      type: 'circle',
                      shape: {
                        cx: resultPoint[0],
                        cy: resultPoint[1],
                        r: 5
                      },
                      style: {
                        fill: aberrant === 'true' ? '#F8C600' : '#000000'
                      }
                    }
                  ]
                }
                return {
                  type: 'group',
                  children: items
                }
              },
              data: this.chartDatas.map(data => {
                return [
                  data['Probe Number'],
                  data['Reference Lower'],
                  data['Reference Upper'],
                  data['Expected Min'],
                  data['Expected Max'],
                  data['Result'],
                  data['Aberrant']
                ]
              })
            }
          ]
        },
        'statistic_chart': {
          xAxis: [
            {
              type: 'category',
              name: this.$t('COMPONENT.digitalMLPA.mainDataChart.chart.axis.title.___Probe'),
              nameLocation: 'middle',
              nameGap: 40,
              nameTextStyle: {
                fontWeight: 'bold'
              },
              axisLabel: {
                fontWeight: 'bold',
                margin: 10,
                rotate: 90
              },
              axisTick: {
                alignWithLabel: true
              },
              boundaryGap: true,
              data: this.chartDatas.map(data => data['Probe Number'].replace('Median ', ''))
            },
            {
              position: 'top',
              axisLabel: {
                show: false
              },
              axisTick: {
                show: false
              },
              axisLine: {
                show: true,
                onZero: false
              }
            }
          ],
          yAxis: [
            {
              type: 'value',
              name: this.$t('COMPONENT.digitalMLPA.mainDataChart.chart.axis.title.___Copy_Number_Ratio'),
              nameLocation: 'middle',
              nameGap: 34,
              nameTextStyle: {
                fontWeight: 'bold'
              },
              max: Math.max(Math.ceil(Math.max(...this.chartDatas.map(data => Number(data['Result']))) * 10) / 10, 1),
              min: Math.min(Math.floor(Math.min(...this.chartDatas.map(data => Number(data['Result']))) * 10) / 10, 1),
              interval: 0.1,
              axisLabel: {
                fontWeight: 'bold',
                formatter: value => value.toFixed(1)
              },
              axisLine: {
                show: true
              },
              splitLine: {
                show: false
              }
            },
            {
              position: 'right',
              axisLabel: {
                show: false
              },
              axisTick: {
                show: false
              },
              axisLine: {
                show: true
              }
            }
          ],
          grid: {
            left: 20,
            right: 5,
            top: 10,
            bottom: 20,
            containLabel: true
          },
          tooltip: {
            trigger: 'axis',
            showContent: false,
            axisPointer: {
              type: 'shadow',
              shadowStyle: {
                color: 'rgba(0, 82, 140, 0.08)'
              }
            }
          },
          series: [
            {
              type: 'scatter',
              silent: true,
              animation: false,
              symbol: 'path://M-5,-10 L-10,-5 L-3,0 L-10,5 L-5,10 L0,3 L5,10 L10,5 L3,0 L10,-5 L5,-10 L0,-3 Z',
              symbolSize: 8,
              itemStyle: {
                color: '#000000'
              },
              emphasis: {
                scale: false
              },
              markLine: {
                silent: true,
                symbol: 'none',
                lineStyle: {
                  type: 'dashed',
                  opacity: 0.3,
                  width: 1
                },
                data: [
                  { yAxis: 1.0 }
                ]
              },
              data: this.chartDatas.map(data => {
                return [
                  data['Probe Number'].replace('Median ', ''),
                  data['Result']
                ]
              })
            }
          ]
        }
      }
    }
  },
  watch: {
    'selectData' (nv) {
      const chart = this.$refs['mlpa-chart']
      chart.dispatchAction({
        type: 'showTip',
        seriesIndex: 0,
        dataIndex: nv ? this.chartDatas.indexOf(this.chartDatas.find(data => data['Probe Number'] === nv['Probe Number'])) : -1
      })
    }
  },
  mounted () {
    const chart = this.$refs['mlpa-chart']
    chart.chart.on('updateAxisPointer', params => {
      this.$emit('select', this.chartDatas[(params || {}).dataIndex])
    })
  },
  methods: {
    chartOnClick (e) {
    },
    chartOnMouseover (e) {
    },
    chartOnMouseout (e) {
    },
    chartOnRendered (e) {
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/digitalMLPA/mainDataChart.vue